/**
 * RealtimeProvider — top-level React context provider for the realtime client.
 *
 * Mount once near the top of the hotel-scoped route tree. Provides the socket
 * instance and the ReconnectManager to all hooks below it. The provider owns
 * the socket lifecycle: it connects on mount and disconnects on unmount.
 *
 * Props:
 *   url         — realtime gateway base URL
 *   getToken    — async getter for the current session token
 *   queryClient — the TanStack QueryClient used for cache writes/invalidation
 *   children    — wrapped routes / components
 */

import type { QueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { registerCacheHandlers } from '../cache';
import { type ConnectionStatus, ReconnectManager } from '../reconnect';
import type { RealtimeSocket } from '../socket';
import { createRealtimeClient } from '../socket';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface RealtimeContextValue {
  socket: RealtimeSocket;
  reconnectManager: ReconnectManager;
  status: ConnectionStatus;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function useRealtimeContext(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) {
    throw new Error('useRealtimeContext must be used inside <RealtimeProvider>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface RealtimeProviderProps {
  url: string;
  getToken: () => string | Promise<string>;
  queryClient: QueryClient;
  children: ReactNode;
}

export function RealtimeProvider({
  url,
  getToken,
  queryClient,
  children,
}: RealtimeProviderProps): ReactNode {
  // Refs keep a stable socket reference across renders without triggering
  // re-renders when the socket emits internal events.
  const socketRef = useRef<RealtimeSocket | null>(null);
  const managerRef = useRef<ReconnectManager | null>(null);
  const cleanupCacheRef = useRef<(() => void) | null>(null);

  const [status, setStatus] = useState<ConnectionStatus>('connecting');

  // Create socket + manager once (StrictMode safe: socket is idempotent).
  if (socketRef.current === null) {
    socketRef.current = createRealtimeClient({ url, getToken });
  }
  if (managerRef.current === null) {
    managerRef.current = new ReconnectManager(socketRef.current, queryClient);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-once effect — socket/manager/queryClient are created once; url/getToken/queryClient changes are not supported post-mount.
  useEffect(() => {
    // Re-create if a previous effect cleanup nulled the refs. Under React
    // StrictMode the effect runs mount → cleanup → mount again WITHOUT a render
    // in between, so the render-time creation above does not re-run and the
    // refs would otherwise be null on the second mount.
    if (socketRef.current === null) {
      socketRef.current = createRealtimeClient({ url, getToken });
    }
    if (managerRef.current === null) {
      managerRef.current = new ReconnectManager(socketRef.current, queryClient);
    }
    const socket = socketRef.current;
    const manager = managerRef.current;

    // Subscribe to status updates so we can re-render the context value.
    const unsubStatus = manager.subscribe(setStatus);

    // Register cache handlers.
    cleanupCacheRef.current = registerCacheHandlers(socket, queryClient);

    // Connect.
    socket.connect();

    return () => {
      unsubStatus();
      cleanupCacheRef.current?.();
      manager.destroy();
      socket.disconnect();
      socketRef.current = null;
      managerRef.current = null;
    };
  }, []); // mount once — url/getToken/queryClient changes are not supported post-mount

  const socket = socketRef.current;
  const manager = managerRef.current;

  // Guard: during the very first render before useEffect fires the refs are set.
  if (!socket || !manager) {
    return null;
  }

  return (
    <RealtimeContext.Provider value={{ socket, reconnectManager: manager, status }}>
      {children}
    </RealtimeContext.Provider>
  );
}
